/**
 * Hook for ticket operations
 */

import {
  createElement,
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { apiClient } from '../services/api';
import type {
  Ticket,
  Message,
  Category,
  ServiceType,
  ServiceTypeOption,
  DashboardStats,
  Attachment,
  ArchivedConversationsResponse,
} from '../types';

function mergeMessagesSorted(a: Message[], b: Message[]): Message[] {
  const map = new Map<number, Message>();
  for (const m of a) {
    map.set(m.id, m);
  }
  for (const m of b) {
    map.set(m.id, m);
  }
  return Array.from(map.values()).sort((x, y) => {
    const c = x.timestamp.localeCompare(y.timestamp);
    if (c !== 0) return c;
    return x.id - y.id;
  });
}

// After a technician send, POST /reply returns success the moment the row
// is inserted — but `telegram_message_id` is written back asynchronously by
// outboundWorker once Telegram returns a message_id. The miniapp's action
// menu (Reply / Edit / Delete) gates on telegramMessageId != null, so a
// single immediate refetch can capture the row before writeback and the
// menu degrades to "Can't delete — sent before this feature shipped" until
// the chat is closed and reopened. We poll the conversation tail a few
// times so the row's telegram_message_id catches up. ~2.4s budget;
// Telegram + writeback is typically well under 1s.
//
// Stop condition: no technician row in the tail younger than 30s is still
// missing telegram_message_id. (Bounding by timestamp avoids waiting on
// pre-v4.1 historical rows whose column was never captured.)
const TELEGRAM_ID_POLL_DELAYS_MS = [0, 400, 800, 1200];
const FRESH_TECH_ROW_WINDOW_MS = 30_000;

async function fetchConvTailUntilTelegramIdPopulated(
  ticketId: string,
  applyMerge: (msgs: Message[]) => void,
): Promise<void> {
  for (const delay of TELEGRAM_ID_POLL_DELAYS_MS) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    try {
      const result = await apiClient.getConversationMessages(ticketId, { limit: 25 });
      if (!result.success || !result.data) continue;
      applyMerge(result.data.messages);

      const now = Date.now();
      const techRowMissingId = result.data.messages.some(
        (m) =>
          m.from === 'technician' &&
          m.telegramMessageId == null &&
          now - new Date(m.timestamp).getTime() <= FRESH_TECH_ROW_WINDOW_MS,
      );
      if (!techRowMissingId) return;
    } catch (err) {
      console.error('Failed to poll conversation tail for telegram_message_id:', err);
    }
  }
}

function asTicketArray(value: unknown): Ticket[] {
  return Array.isArray(value) ? (value as Ticket[]) : [];
}

function asArchivedConversationRows(
  value: unknown,
): ArchivedConversationsResponse['conversations'] {
  return Array.isArray(value) ? (value as ArchivedConversationsResponse['conversations']) : [];
}

function normalizeListPagination(
  raw: unknown,
  fallbackPage: number,
): { page: number; totalPages: number; total: number } {
  const p = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!p) {
    return { page: Math.max(1, fallbackPage), totalPages: 1, total: 0 };
  }
  const page = Number(p.page);
  const totalPages = Number(p.totalPages);
  const total = Number(p.total);
  return {
    page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : Math.max(1, fallbackPage),
    totalPages: Number.isFinite(totalPages) && totalPages >= 1 ? Math.floor(totalPages) : 1,
    total: Number.isFinite(total) && total >= 0 ? Math.floor(total) : 0,
  };
}

/** Resolved-tab variant — adds `totalTickets` so the badge can show a
 *  ticket count separate from the (user-grouped) `total`. Defensive parse:
 *  if the server didn't send the field, totalTickets falls back to `total`
 *  so a half-rolled-out server doesn't render a zero badge. */
function normalizeArchivedPagination(
  raw: unknown,
  fallbackPage: number,
): { page: number; totalPages: number; total: number; totalTickets: number } {
  const base = normalizeListPagination(raw, fallbackPage);
  const p = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const rawTotalTickets = p ? Number(p.totalTickets) : NaN;
  const totalTickets = Number.isFinite(rawTotalTickets) && rawTotalTickets >= 0
    ? Math.floor(rawTotalTickets)
    : base.total;
  return { ...base, totalTickets };
}

interface UseTicketsReturn {
  // Data
  openTickets: Ticket[];
  archivedTickets: Ticket[];
  archivedConversations: ArchivedConversationsResponse['conversations'];
  currentTicket: Ticket | null;
  categories: Category[];
  serviceTypes: ServiceTypeOption[];
  stats: DashboardStats | null;

  // Pagination — open/in_progress tabs
  openPagination: {
    page: number;
    totalPages: number;
    total: number;
  };

  // Pagination — resolved tab
  archivedPagination: {
    page: number;
    totalPages: number;
    /** Distinct users matching the filter — paginates the user list. */
    total: number;
    /** Matching closed tickets across all those users — drives the
     *  Resolved tab badge so the number reflects tickets, not users.
     *  `null` until the first archived-conversations fetch resolves, so
     *  TicketList can distinguish "not loaded yet → fall back to
     *  stats.closed" from "loaded and zero matches → show 0". */
    totalTickets: number | null;
  };

  // Loading states
  isLoading: boolean;
  isLoadingTicket: boolean;
  isSending: boolean;

  // Error state
  error: string | null;

  // Actions
  refreshOpenTickets: (opts?: {
    status?: 'open' | 'in_progress' | 'escalated';
    page?: number;
    sort?: 'unread' | 'newest' | 'oldest' | 'activity';
    from?: string;
    to?: string;
    q?: string;
  }) => Promise<void>;
  loadArchivedTickets: (page?: number) => Promise<void>;
  loadArchivedConversations: (opts?: {
    page?: number;
    q?: string;
    from?: string;
    to?: string;
    categories?: string[];
    hasFollowup?: boolean;
    sort?: 'most_recent' | 'most_tickets' | 'name_az';
  }) => Promise<void>;
  loadTicket: (ticketId: string) => Promise<void>;
  /** First page of unified conversation (newest messages) */
  loadConversationInitial: (ticketId: string) => Promise<void>;
  /** Older page when user scrolls up */
  loadConversationOlder: (ticketId: string) => Promise<void>;
  /** Merge latest page (e.g. after WS or send) without dropping loaded history */
  mergeConversationLatestTail: (ticketId: string) => Promise<void>;
  conversationMessages: Message[];
  conversationHasMore: boolean;
  conversationLoadingInitial: boolean;
  conversationLoadingOlder: boolean;
  loadCategories: () => Promise<void>;
  loadServiceTypes: () => Promise<void>;
  loadStats: (opts?: { from?: string; to?: string; q?: string }) => Promise<void>;
  /** Pin a ticket for the calling tech. Optimistic — flips local isPinned
   *  immediately, then awaits the server. On cap-reached / network error,
   *  rolls back and returns the error message for UI display. */
  pinTicket: (ticketId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Unpin — same shape as pinTicket. */
  unpinTicket: (ticketId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Reorder pins for the calling tech. `ticketIds` is the new top-first
   *  display order (must contain exactly the tech's currently-pinned ids). */
  reorderPins: (ticketIds: string[]) => Promise<{ ok: true } | { ok: false; error: string }>;
  sendReply: (ticketId: string, message: string, replyToMessageId?: number) => Promise<boolean>;
  sendReplyWithAttachments: (ticketId: string, message: string, attachments: Attachment[], replyToMessageId?: number) => Promise<boolean>;
  /** Edit-message phase — PATCH a technician message's text/caption. On
   *  success the corresponding row in conversationMessages is optimistically
   *  updated so the bubble reflects the new text immediately, no refetch
   *  needed. Returns false + sets `error` on any 4xx/5xx. */
  editMessage: (ticketId: string, messageId: number, text: string) => Promise<boolean>;
  markAsRead: (ticketId: string) => Promise<void>;
  closeTicket: (ticketId: string, serviceTypes: ServiceType[], categories: string[], closeNote?: string) => Promise<boolean>;
  /** Close N tickets at once with shared close metadata. Returns the IDs
   *  that were actually closed (some may have been already-closed and got
   *  silently skipped by the server). */
  closeTicketsBatch: (
    ticketIds: string[],
    serviceTypes: ServiceType[],
    categories: string[],
    closeNote?: string,
  ) => Promise<{ ok: boolean; closedTicketIds: string[] }>;
  /** Load the user's currently-active tickets (open/in_progress/escalated).
   *  Stored in `userActiveTickets`. */
  loadUserActiveTickets: (userId: number) => Promise<void>;
  userActiveTickets: Ticket[];
  clearCurrentTicket: () => void;
  clearError: () => void;
  /** Clears conversation state and pins session to this ticketId before parallel ticket+message loads. */
  beginTicketDetailSession: (ticketId: string) => void;
  /** Seeds header/metadata from list row so detail can render without full-screen ticket spinner. */
  hydrateTicketFromListRow: (ticket: Ticket) => void;
}

const TicketsContext = createContext<UseTicketsReturn | null>(null);

function useTicketsImpl(): UseTicketsReturn {
  const [openTickets, setOpenTickets] = useState<Ticket[]>([]);
  const [archivedTickets, setArchivedTickets] = useState<Ticket[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<ArchivedConversationsResponse['conversations']>([]);
  const [currentTicket, setCurrentTicket] = useState<Ticket | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  /** User's currently-active tickets, populated on demand by the
   *  multi-select close flow. Empty array until loadUserActiveTickets fires. */
  const [userActiveTickets, setUserActiveTickets] = useState<Ticket[]>([]);

  const [openPagination, setOpenPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  const [archivedPagination, setArchivedPagination] = useState<{
    page: number;
    totalPages: number;
    total: number;
    totalTickets: number | null;
  }>({
    page: 1,
    totalPages: 1,
    total: 0,
    // `null` (not 0) so the Resolved tab badge can fall back to stats.closed
    // until the first archived fetch lands. Otherwise initial render shows 0
    // for a user who actually has resolved tickets.
    totalTickets: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [conversationMessages, setConversationMessages] = useState<Message[]>([]);
  const [conversationHasMore, setConversationHasMore] = useState(false);
  const [conversationOlderCursor, setConversationOlderCursor] = useState<{
    beforeTimestamp: string;
    beforeId: number;
  } | null>(null);
  const [conversationLoadingInitial, setConversationLoadingInitial] = useState(false);
  const [conversationLoadingOlder, setConversationLoadingOlder] = useState(false);

  const prevLoadedTicketIdRef = useRef<string | null>(null);
  const convPagingRef = useRef({
    hasMore: false,
    cursor: null as { beforeTimestamp: string; beforeId: number } | null,
  });
  const loadingOlderInFlightRef = useRef(false);
  /** Mirrors `currentTicket` for synchronous reads inside async callbacks (hydrate vs loadTicket). */
  const currentTicketRef = useRef<Ticket | null>(null);

  useEffect(() => {
    convPagingRef.current = {
      hasMore: conversationHasMore,
      cursor: conversationOlderCursor,
    };
  }, [conversationHasMore, conversationOlderCursor]);

  useEffect(() => {
    currentTicketRef.current = currentTicket;
  }, [currentTicket]);

  const beginTicketDetailSession = useCallback((ticketId: string) => {
    setConversationMessages([]);
    setConversationHasMore(false);
    setConversationOlderCursor(null);
    prevLoadedTicketIdRef.current = ticketId;
  }, []);

  const hydrateTicketFromListRow = useCallback((ticket: Ticket) => {
    currentTicketRef.current = ticket;
    setCurrentTicket(ticket);
    prevLoadedTicketIdRef.current = ticket.ticketId;
  }, []);

  // Load open tickets (paginated, 100 per page — matches server TICKET_LIST_PAGE_SIZE).
  //
  //   - `status` scopes to a single bucket (the tab list view does this so
  //     the per-bucket LIMIT can't starve one status out of the response).
  //   - `page` selects the 1-based page; defaults to 1.
  //   - `sort` picks the ORDER BY (default `unread`).
  //   - `from` / `to` filter `created_at` to a YYYY-MM-DD range (inclusive).
  //
  // Omit everything for the dashboard's unified recent-activity view.
  const refreshOpenTickets = useCallback(async (
    opts: {
      status?: 'open' | 'in_progress' | 'escalated';
      page?: number;
      sort?: 'unread' | 'newest' | 'oldest' | 'activity';
      from?: string;
      to?: string;
      q?: string;
    } = {},
  ) => {
    setIsLoading(true);
    setError(null);

    const page = opts.page ?? 1;
    try {
      const result = await apiClient.getOpenTickets(opts);

      if (result.success && result.data) {
        setOpenTickets(asTicketArray(result.data.tickets));
        setOpenPagination(normalizeListPagination(result.data.pagination, page));
      } else {
        setError(result.error || 'Failed to load tickets');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load archived tickets
  const loadArchivedTickets = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiClient.getArchivedTickets(page);

      if (result.success && result.data) {
        setArchivedTickets(asTicketArray(result.data.tickets));
        setArchivedPagination(normalizeArchivedPagination(result.data.pagination, page));
      } else {
        setError(result.error || 'Failed to load archived tickets');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load archived tickets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sequence ref used to discard out-of-order responses when the user rapidly
  // toggles filters (e.g. clicks several category chips in quick succession).
  // Each call increments; only the latest call's response is applied. Without
  // this, an older slower request can arrive AFTER a newer one and clobber
  // the UI with stale data. Issue #100/#102.
  const archivedFetchSeqRef = useRef(0);

  // Load archived conversations (grouped by user). Accepts the same filter
  // shape as the API client — passes through verbatim.
  const loadArchivedConversations = useCallback(async (opts: {
    page?: number;
    q?: string;
    from?: string;
    to?: string;
    categories?: string[];
    hasFollowup?: boolean;
    sort?: 'most_recent' | 'most_tickets' | 'name_az';
  } = {}) => {
    const page = opts.page ?? 1;
    const mySeq = ++archivedFetchSeqRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiClient.getArchivedConversations(opts);

      // Stale-response guard — a newer fetch superseded us, drop this result.
      if (mySeq !== archivedFetchSeqRef.current) return;

      if (result.success && result.data) {
        setArchivedConversations(asArchivedConversationRows(result.data.conversations));
        setArchivedPagination(normalizeArchivedPagination(result.data.pagination, page));
      } else {
        setError(result.error || 'Failed to load archived conversations');
      }
    } catch (err) {
      if (mySeq !== archivedFetchSeqRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load archived conversations');
    } finally {
      // Only the latest fetch should clear the loading flag; an earlier
      // straggler resolving shouldn't suggest a newer in-flight is done.
      if (mySeq === archivedFetchSeqRef.current) setIsLoading(false);
    }
  }, []);

  // Load a single ticket
  const loadTicket = useCallback(async (ticketId: string) => {
    const alreadyShowing = currentTicketRef.current?.ticketId === ticketId;
    if (!alreadyShowing) {
      setIsLoadingTicket(true);
    }
    setError(null);

    try {
      const result = await apiClient.getTicket(ticketId);

      if (result.success && result.data) {
        prevLoadedTicketIdRef.current = ticketId;
        const t = result.data.ticket;
        currentTicketRef.current = t;
        setCurrentTicket(t);
      } else {
        setError(result.error || 'Failed to load ticket');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setIsLoadingTicket(false);
    }
  }, []);

  const loadConversationInitial = useCallback(async (ticketId: string) => {
    setConversationLoadingInitial(true);
    setError(null);
    try {
      const result = await apiClient.getConversationMessages(ticketId, { limit: 25 });
      if (prevLoadedTicketIdRef.current !== ticketId) return;
      if (result.success && result.data) {
        setConversationMessages(result.data.messages);
        setConversationHasMore(result.data.hasMore);
        setConversationOlderCursor(result.data.nextCursor);
      } else {
        setError(result.error || 'Failed to load messages');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setConversationLoadingInitial(false);
    }
  }, []);

  const loadConversationOlder = useCallback(async (ticketId: string) => {
    const { hasMore, cursor } = convPagingRef.current;
    if (!hasMore || !cursor || loadingOlderInFlightRef.current) return;
    loadingOlderInFlightRef.current = true;
    setConversationLoadingOlder(true);
    try {
      const result = await apiClient.getConversationMessages(ticketId, {
        beforeTimestamp: cursor.beforeTimestamp,
        beforeId: cursor.beforeId,
        limit: 25,
      });
      if (prevLoadedTicketIdRef.current !== ticketId) return;
      if (result.success && result.data) {
        setConversationMessages((prev) => mergeMessagesSorted(prev, result.data!.messages));
        setConversationHasMore(result.data.hasMore);
        setConversationOlderCursor(result.data.nextCursor);
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      loadingOlderInFlightRef.current = false;
      setConversationLoadingOlder(false);
    }
  }, []);

  const mergeConversationLatestTail = useCallback(async (ticketId: string) => {
    try {
      const result = await apiClient.getConversationMessages(ticketId, { limit: 25 });
      if (prevLoadedTicketIdRef.current !== ticketId) return;
      if (result.success && result.data) {
        setConversationMessages((prev) => mergeMessagesSorted(prev, result.data!.messages));
      }
    } catch (err) {
      console.error('Failed to merge conversation tail:', err);
    }
  }, []);
  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const result = await apiClient.getCategories();

      if (result.success && result.data) {
        setCategories(result.data.categories);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Load service types (Step 1 of close-ticket modal)
  const loadServiceTypes = useCallback(async () => {
    try {
      const result = await apiClient.getServiceTypes();

      if (result.success && result.data) {
        setServiceTypes(result.data.serviceTypes);
      }
    } catch (err) {
      console.error('Failed to load service types:', err);
    }
  }, []);

  // Load dashboard stats. Optional filter args narrow the per-status counts
  // so the TicketList tab badges can stay in lock-step with the list under
  // the active filter. Dashboard calls this with no args (global counts).
  const loadStats = useCallback(async (opts: { from?: string; to?: string; q?: string } = {}) => {
    try {
      const result = await apiClient.getDashboardStats(opts);

      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  // Send a text reply. Phase 33: optional replyToMessageId threads the
  // target's DB id through to the apiClient, which the server resolves
  // to a Telegram message_id and attaches as reply_parameters.
  /**
   * Edit-message phase — PATCH a technician message and apply the new
   * text to the in-memory conversation. Optimistic in the sense that we
   * map the row's text the moment the server returns 200; no full refetch
   * (would be wasteful and would lose the user's scroll position).
   *
   * Permission gate (L1 author-only vs L2 any-tech) is enforced server-
   * side; the UI hides the Edit button when ineligible, so a 403 here
   * shouldn't surface in normal use.
   */
  const editMessage = useCallback(async (
    ticketId: string,
    messageId: number,
    text: string,
  ): Promise<boolean> => {
    setError(null);
    try {
      const result = await apiClient.editMessage(ticketId, messageId, text);
      if (result.success) {
        setConversationMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, text } : m)),
        );
        return true;
      }
      setError(result.error || 'Failed to edit message');
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit message');
      return false;
    }
  }, []);

  const sendReply = useCallback(async (
    ticketId: string,
    message: string,
    replyToMessageId?: number,
  ): Promise<boolean> => {
    setIsSending(true);
    setError(null);

    try {
      const result = await apiClient.sendReply(ticketId, message, replyToMessageId);

      if (result.success) {
        await loadTicket(ticketId);
        await fetchConvTailUntilTelegramIdPopulated(ticketId, (msgs) => {
          if (prevLoadedTicketIdRef.current !== ticketId) return;
          setConversationMessages((prev) => mergeMessagesSorted(prev, msgs));
        });
        return true;
      } else {
        setError(result.error || 'Failed to send reply');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
      return false;
    } finally {
      setIsSending(false);
    }
  }, [loadTicket]);

  // Send reply with attachments. Phase 33: optional replyToMessageId is
  // forwarded — only the text portion carries reply_parameters today
  // (attachment uploads via /media don't, by design — see api.ts JSDoc).
  const sendReplyWithAttachments = useCallback(
    async (
      ticketId: string,
      message: string,
      attachments: Attachment[],
      replyToMessageId?: number,
    ): Promise<boolean> => {
      setIsSending(true);
      setError(null);

      try {
        const result = await apiClient.sendReplyWithAttachments(
          ticketId,
          message,
          attachments,
          replyToMessageId,
        );

        if (result.success) {
          await loadTicket(ticketId);
          await fetchConvTailUntilTelegramIdPopulated(ticketId, (msgs) => {
            if (prevLoadedTicketIdRef.current !== ticketId) return;
            setConversationMessages((prev) => mergeMessagesSorted(prev, msgs));
          });
          return true;
        } else {
          setError(result.error || 'Failed to send reply');
          return false;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send reply');
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [loadTicket]
  );

  // Mark ticket as read
  const markAsRead = useCallback(async (ticketId: string) => {
    try {
      await apiClient.markAsRead(ticketId);

      // Update local state
      setOpenTickets(prev =>
        (Array.isArray(prev) ? prev : []).map(t =>
          t.ticketId === ticketId ? { ...t, hasUnreadMessages: false } : t
        )
      );
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Close ticket
  const closeTicket = useCallback(
    async (ticketId: string, selectedServiceTypes: ServiceType[], selectedCategories: string[], closeNote?: string): Promise<boolean> => {
      setIsSending(true);
      setError(null);

      try {
        const result = await apiClient.closeTicket(ticketId, selectedServiceTypes, selectedCategories, closeNote);

        if (result.success) {
          // Remove from open tickets
          setOpenTickets(prev => (Array.isArray(prev) ? prev : []).filter(t => t.ticketId !== ticketId));
          currentTicketRef.current = null;
          setCurrentTicket(null);
          setConversationMessages([]);
          setConversationHasMore(false);
          setConversationOlderCursor(null);
          prevLoadedTicketIdRef.current = null;
          return true;
        } else {
          setError(result.error || 'Failed to close ticket');
          return false;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to close ticket');
        return false;
      } finally {
        setIsSending(false);
      }
    },
    []
  );

  // Close N tickets at once with shared metadata. Mirrors closeTicket's
  // state cleanup but for a Set of ticket IDs. The server may close fewer
  // than requested (already-closed IDs are silently skipped), so the
  // closedTicketIds returned by the API are the source of truth for
  // optimistic-updating the local lists.
  const closeTicketsBatch = useCallback(
    async (
      ticketIds: string[],
      selectedServiceTypes: ServiceType[],
      selectedCategories: string[],
      closeNote?: string,
    ): Promise<{ ok: boolean; closedTicketIds: string[] }> => {
      setIsSending(true);
      setError(null);
      try {
        const result = await apiClient.closeTicketBatch(
          ticketIds,
          selectedServiceTypes,
          selectedCategories,
          closeNote,
        );
        if (result.success && result.data) {
          const closedSet = new Set(result.data.closedTicketIds);
          // Drop any closed ticket from the open-tickets list.
          setOpenTickets((prev) =>
            (Array.isArray(prev) ? prev : []).filter((t) => !closedSet.has(t.ticketId)),
          );
          // Drop from the user's active-tickets cache too (drives the
          // selection UI's checkbox visibility).
          setUserActiveTickets((prev) => prev.filter((t) => !closedSet.has(t.ticketId)));
          // If the currently-focused ticket was in the batch, clear it so
          // the detail view doesn't show stale "open" state.
          if (currentTicketRef.current && closedSet.has(currentTicketRef.current.ticketId)) {
            currentTicketRef.current = null;
            setCurrentTicket(null);
          }
          return { ok: true, closedTicketIds: result.data.closedTicketIds };
        }
        setError(result.error || 'Failed to close tickets');
        return { ok: false, closedTicketIds: [] };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to close tickets');
        return { ok: false, closedTicketIds: [] };
      } finally {
        setIsSending(false);
      }
    },
    [],
  );

  // Load the user's currently-active tickets — used to mark which dividers
  // in the unified conversation history are selectable for close. Fire on
  // demand (when the user enters selection mode) rather than on every
  // ticket-detail mount; the data is only needed for the multi-close UX.
  const loadUserActiveTickets = useCallback(async (userId: number) => {
    try {
      const result = await apiClient.getUserActiveTickets(userId);
      if (result.success && result.data) {
        setUserActiveTickets(asTicketArray(result.data.tickets));
      }
    } catch (err) {
      console.error('Failed to load user active tickets:', err);
    }
  }, []);

  // Clear current ticket
  const clearCurrentTicket = useCallback(() => {
    currentTicketRef.current = null;
    setCurrentTicket(null);
    setConversationMessages([]);
    setConversationHasMore(false);
    setConversationOlderCursor(null);
    prevLoadedTicketIdRef.current = null;
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ── Pin mutations ────────────────────────────────────────────────────
  // Optimistic local flip — keeps the row in place visually while the
  // server confirms. The actual pinned-first ordering comes from the next
  // server refetch (triggered by the caller via refreshOpenTickets /
  // loadArchivedConversations). The local flip just makes the 📌 icon
  // appear instantly so the action feels responsive.
  const flipLocalPin = useCallback((ticketId: string, isPinned: boolean) => {
    setOpenTickets((prev) =>
      Array.isArray(prev)
        ? prev.map((t) => (t.ticketId === ticketId ? { ...t, isPinned } : t))
        : prev,
    );
    setArchivedConversations((prev) =>
      Array.isArray(prev)
        ? prev.map((row) =>
            row.ticket.ticketId === ticketId
              ? { ...row, ticket: { ...row.ticket, isPinned } }
              : row,
          )
        : prev,
    );
  }, []);

  const pinTicket = useCallback(async (ticketId: string) => {
    flipLocalPin(ticketId, true);
    const result = await apiClient.pinTicket(ticketId);
    if (!result.success) {
      flipLocalPin(ticketId, false); // roll back
      return { ok: false as const, error: result.error || 'Failed to pin ticket' };
    }
    return { ok: true as const };
  }, [flipLocalPin]);

  const unpinTicket = useCallback(async (ticketId: string) => {
    flipLocalPin(ticketId, false);
    const result = await apiClient.unpinTicket(ticketId);
    if (!result.success) {
      flipLocalPin(ticketId, true); // roll back
      return { ok: false as const, error: result.error || 'Failed to unpin ticket' };
    }
    return { ok: true as const };
  }, [flipLocalPin]);

  const reorderPins = useCallback(async (ticketIds: string[]) => {
    const result = await apiClient.reorderPins(ticketIds);
    if (!result.success) {
      return { ok: false as const, error: result.error || 'Failed to reorder pins' };
    }
    return { ok: true as const };
  }, []);

  return {
    openTickets,
    archivedTickets,
    archivedConversations,
    currentTicket,
    categories,
    serviceTypes,
    stats,
    openPagination,
    archivedPagination,
    isLoading,
    isLoadingTicket,
    isSending,
    error,
    refreshOpenTickets,
    loadArchivedTickets,
    loadArchivedConversations,
    loadTicket,
    loadConversationInitial,
    loadConversationOlder,
    mergeConversationLatestTail,
    conversationMessages,
    conversationHasMore,
    conversationLoadingInitial,
    conversationLoadingOlder,
    loadCategories,
    loadServiceTypes,
    loadStats,
    sendReply,
    sendReplyWithAttachments,
    editMessage,
    markAsRead,
    closeTicket,
    closeTicketsBatch,
    loadUserActiveTickets,
    userActiveTickets,
    clearCurrentTicket,
    clearError,
    beginTicketDetailSession,
    hydrateTicketFromListRow,
    pinTicket,
    unpinTicket,
    reorderPins,
  };
}

/** Single tickets + stats store for Dashboard, TicketList, and TicketDetail (state survives detail navigation). */
export function TicketsProvider({ children }: { children: ReactNode }) {
  const value = useTicketsImpl();
  return createElement(TicketsContext.Provider, { value }, children);
}

export function useTickets(): UseTicketsReturn {
  const ctx = useContext(TicketsContext);
  if (ctx == null) {
    throw new Error('useTickets must be used within a TicketsProvider');
  }
  return ctx;
}
